import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import {
  Box, Button, Chip, CircularProgress, IconButton, Stack,
  Switch, Tooltip, Typography, FormControlLabel, RadioGroup, Radio
} from '@mui/material'
import { FormInput } from '../components/ui'
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Star as DefaultIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { salesStagesApi, SalesStage } from '../lib/api/salesStagesApi'
import { PageHeader } from '../components/shared/PageHeader'
import { EntityModal } from '../components/shared/EntityModal'
import { DeleteDialog } from '../components/shared/DeleteDialog'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'
import { themeColors } from '../app/layout/theme'
import { authStore } from '../lib/stores/authStore'
import { usePermission } from '../lib/hooks/usePermission'

// ── Colour palette ────────────────────────────────────────────────────────────

const PRESET_COLORS = [
  '#6366f1', '#3b82f6', '#0ea5e9', '#14b8a6',
  '#16a34a', '#84cc16', '#eab308', '#f97316',
  '#ef4444', '#ec4899', '#7c3aed', '#64748b',
]

function ColorDot({ color, size = 20 }: { color: string; size?: number }) {
  return <Box sx={{ width: size, height: size, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
}

function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <Box>
      <Typography fontSize="12px" color="text.secondary" fontWeight={600} mb={1}>Stage Colour</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {PRESET_COLORS.map(c => (
          <Box key={c} onClick={() => onChange(c)} sx={{
            width: 28, height: 28, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
            border: value === c ? '3px solid #1d4ed8' : '2px solid transparent',
            outline: value === c ? '2px solid #fff' : 'none',
            boxShadow: value === c ? `0 0 0 3px ${c}60` : 'none',
            transition: 'all 0.15s',
            '&:hover': { transform: 'scale(1.18)' },
          }} />
        ))}
      </Box>
    </Box>
  )
}

// ── Stage Form Modal (Add / Edit) ─────────────────────────────────────────────

interface StageFormValues {
  name:          string
  color:         string
  type:          'active' | 'terminal'
  completionPct: string
}

function StageFormModal({ open, onClose, stage }: {
  open:    boolean
  onClose: () => void
  stage?:  SalesStage   // undefined = create mode
}) {
  const qc = useQueryClient()
  const isEdit = !!stage

  const [form, setForm] = useState<StageFormValues>(() => ({
    name:          stage?.name  ?? '',
    color:         stage?.color ?? '#6366f1',
    type:          stage?.type  ?? 'active',
    completionPct: stage?.completionPct != null ? String(stage.completionPct) : '',
  }))

  const set = (k: keyof StageFormValues, v: string) =>
    setForm(f => ({ ...f, [k]: v }))

  const createMut = useMutation({
    mutationFn: () => salesStagesApi.createStage({
      name:          form.name.trim(),
      type:          form.type,
      color:         form.color,
      completionPct: form.type === 'active' && form.completionPct ? parseInt(form.completionPct) : null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-stages'] })
      toast.success('Sales stage created')
      onClose()
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to create stage'),
  })

  const updateMut = useMutation({
    mutationFn: () => salesStagesApi.updateStage(stage!.id, {
      name:          form.name.trim(),
      type:          form.type,
      color:         form.color,
      completionPct: form.type === 'active' && form.completionPct ? parseInt(form.completionPct) : null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-stages'] })
      toast.success('Sales stage updated')
      onClose()
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update stage'),
  })

  const isPending = createMut.isPending || updateMut.isPending

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      onSubmit={() => isEdit ? updateMut.mutate() : createMut.mutate()}
      title={isEdit ? 'Edit Sales Stage' : 'New Sales Stage'}
      submitLabel={isEdit ? 'Save Changes' : 'Create Stage'}
      isSubmitting={isPending}
      submitDisabled={!form.name.trim()}
      maxWidth="xs"
      icon={isEdit ? <EditIcon sx={{ fontSize: 18 }} /> : <AddIcon sx={{ fontSize: 18 }} />}
    >
      <Stack spacing={2.5}>
        {/* Name */}
        <FormInput
          label="Stage Name *"
          value={form.name}
          onChange={e => set('name', e.target.value)}
          fullWidth
          placeholder="e.g. Invoice Sent"
          autoFocus
        />

        {/* Colour picker */}
        <Box>
          <ColorPicker value={form.color} onChange={c => set('color', c)} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
            <ColorDot color={form.color} />
            <Typography fontSize="12px" color="text.secondary">Preview</Typography>
            <Chip label={form.name || 'Stage Name'} size="small" sx={{
              bgcolor: `${form.color}22`, color: form.color,
              fontWeight: 700, fontSize: '11px', borderRadius: '4px',
              border: `1px solid ${form.color}44`,
            }} />
          </Box>
        </Box>

        {/* Completion % */}
        {form.type === 'active' && (
          <FormInput
            label="Completion %"
            value={form.completionPct}
            onChange={e => set('completionPct', e.target.value)}
            type="number"
            fullWidth
            placeholder="e.g. 50"
            inputProps={{ min: 0, max: 100 }}
            helperText="Completion percentage for this stage (0-100)"
          />
        )}

        {/* Stage Type */}
        <Box sx={{ p: 1.5, borderRadius: '10px', border: `1px solid ${themeColors.border}`, bgcolor: '#fafafa' }}>
          <Typography fontSize="12px" fontWeight={600} color="text.secondary" mb={1}>
            Stage Type
          </Typography>
          <RadioGroup
            value={form.type}
            onChange={e => set('type', e.target.value)}
            row
          >
            <FormControlLabel
              value="active"
              control={<Radio size="small" />}
              label={<Typography fontSize="13px">Active Flow</Typography>}
              disabled={stage?.isDefault} // default stage must remain active
            />
            <FormControlLabel
              value="terminal"
              control={<Radio size="small" />}
              label={<Typography fontSize="13px">Terminal Stage</Typography>}
              disabled={stage?.isDefault}
            />
          </RadioGroup>
          {stage?.isDefault && (
            <Typography fontSize="11px" color="text.disabled" sx={{ mt: 1 }}>
              * The default stage must remain an Active stage.
            </Typography>
          )}
        </Box>
      </Stack>
    </EntityModal>
  )
}

// ── Stage Row ─────────────────────────────────────────────────────────────────

function StageRow({
  stage, onEdit, onDelete, canEdit, canDelete,
  onDragStart, onDragEnd, onDragOver, onDrop, isDragging
}: {
  stage:       SalesStage
  onEdit:      (s: SalesStage) => void
  onDelete:    (s: SalesStage) => void
  canEdit:     boolean
  canDelete:   boolean
  onDragStart: (e: React.DragEvent) => void
  onDragEnd:   (e: React.DragEvent) => void
  onDragOver:  (e: React.DragEvent) => void
  onDrop:      (e: React.DragEvent) => void
  isDragging:  boolean
}) {
  const isReorderable = stage.type === 'active' && canEdit

  return (
    <Box
      draggable={isReorderable}
      onDragStart={isReorderable ? onDragStart : undefined}
      onDragEnd={isReorderable ? onDragEnd : undefined}
      onDragOver={isReorderable ? onDragOver : undefined}
      onDrop={isReorderable ? onDrop : undefined}
      sx={{
        display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5,
        border: `1px solid ${themeColors.border}`, borderRadius: '10px', bgcolor: '#fff',
        transition: 'all 0.15s',
        opacity: isDragging ? 0.4 : 1,
        cursor: isReorderable ? 'grab' : 'default',
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderColor: '#cbd5e1' },
      }}
    >
      {/* Drag handle */}
      {isReorderable ? (
        <DragIcon sx={{ fontSize: 18, color: '#d1d5db', flexShrink: 0 }} />
      ) : (
        <Box sx={{ width: 18, height: 18, flexShrink: 0 }} />
      )}

      {/* Position representation */}
      {stage.type === 'active' && (
        <Box sx={{
          width: 24, height: 24, borderRadius: '50%', bgcolor: '#f1f5f9',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Typography fontSize="11px" fontWeight={700} color="text.secondary">{stage.position}</Typography>
        </Box>
      )}

      {/* Color dot + name */}
      <ColorDot color={stage.color} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={stage.name} size="small" sx={{
            bgcolor: `${stage.color}22`, color: stage.color,
            fontWeight: 700, fontSize: '12px', borderRadius: '6px',
            border: `1px solid ${stage.color}44`, height: 24,
            '& .MuiChip-label': { px: '8px' },
          }} />
          {stage.isDefault && (
            <Chip icon={<DefaultIcon sx={{ fontSize: 12, '&.MuiChip-icon': { color: '#eab308' } }} />}
              label="Default Stage" size="small" sx={{ height: 20, fontSize: '10px', fontWeight: 700,
                bgcolor: '#fefbeb', color: '#854d0e', borderRadius: '4px', '& .MuiChip-label': { pl: '2px', pr: '6px' } }} />
          )}
          <Chip
            label={stage.type === 'active' ? 'Active' : 'Terminal'}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '10px', textTransform: 'capitalize', fontWeight: 600, color: 'text.secondary' }}
          />
        </Box>
      </Box>

      {/* Completion % */}
      {stage.type === 'active' && (
        stage.completionPct != null ? (
          <Typography fontSize="12.5px" fontWeight={600} color="text.secondary" sx={{ flexShrink: 0, mr: 1 }}>
            {stage.completionPct}%
          </Typography>
        ) : (
          <Typography fontSize="11px" color="text.disabled" sx={{ flexShrink: 0, mr: 1 }}>—</Typography>
        )
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
        {canEdit && (
          <Tooltip title="Edit stage">
            <IconButton size="small" onClick={() => onEdit(stage)} sx={{ color: '#9ca3af', '&:hover': { color: themeColors.primary, bgcolor: '#F2EEF9' } }}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {canDelete && !stage.isDefault && (
          <Tooltip title="Delete stage">
            <IconButton size="small" onClick={() => onDelete(stage)} sx={{ color: '#9ca3af', '&:hover': { color: '#dc2626' } }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function SalesStagesPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  // Sales settings are general admin options. Check role / permissions
  const { hasAction, isSuperAdmin } = usePermission()
  const canCreate = hasAction('/settings', 'create') || isSuperAdmin
  const canEdit   = hasAction('/settings', 'update') || isSuperAdmin
  const canDelete = hasAction('/settings', 'delete') || isSuperAdmin

  const [addOpen,    setAddOpen]    = useState(false)
  const [editStage,  setEditStage]  = useState<SalesStage | null>(null)
  const [deleteStage, setDeleteStage] = useState<SalesStage | null>(null)

  const { data: stages = [], isLoading } = useQuery({
    queryKey: ['sales-stages'],
    queryFn:  () => salesStagesApi.getStages(),
    staleTime: 30_000,
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => salesStagesApi.deleteStage(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-stages'] })
      toast.success('Sales stage deleted successfully')
      setDeleteStage(null)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete stage'),
  })

  const [localStages, setLocalStages] = useState<SalesStage[]>([])
  const [draggedStageId, setDraggedStageId] = useState<number | null>(null)
  const [isReorderConfirmOpen, setIsReorderConfirmOpen] = useState(false)

  useEffect(() => {
    if (stages) {
      setLocalStages([...stages].sort((a, b) => {
        if (a.type !== b.type) return a.type === 'active' ? -1 : 1
        return a.position - b.position
      }))
    }
  }, [stages])

  const reorderMut = useMutation({
    mutationFn: (positions: { id: number; position: number }[]) => salesStagesApi.reorderStages(positions),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-stages'] })
      toast.success('Active stages reordered successfully')
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? 'Failed to reorder stages')
      if (stages) {
        setLocalStages([...stages].sort((a, b) => {
          if (a.type !== b.type) return a.type === 'active' ? -1 : 1
          return a.position - b.position
        }))
      }
    },
  })

  const hasChanges = useMemo(() => {
    if (stages.length !== localStages.length) return false
    const sortedOriginal = [...stages].sort((a, b) => {
      if (a.type !== b.type) return a.type === 'active' ? -1 : 1
      return a.position - b.position
    })
    return localStages.some((s, idx) => s.id !== sortedOriginal[idx]?.id)
  }, [stages, localStages])

  const handleDiscardChanges = () => {
    if (stages) {
      setLocalStages([...stages].sort((a, b) => {
        if (a.type !== b.type) return a.type === 'active' ? -1 : 1
        return a.position - b.position
      }))
    }
  }

  const handleConfirmSaveOrder = () => {
    setIsReorderConfirmOpen(false)
    const payload = localStages
      .filter(s => s.type === 'active')
      .map((s, idx) => ({
        id: s.id,
        position: idx + 1
      }))
    reorderMut.mutate(payload)
  }

  const handleDragStart = (e: React.DragEvent, id: number) => {
    setDraggedStageId(id)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragEnd = () => {
    setDraggedStageId(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent, targetId: number) => {
    e.preventDefault()
    if (draggedStageId === null || draggedStageId === targetId) return

    const draggedStage = localStages.find(s => s.id === draggedStageId)
    const targetStage = localStages.find(s => s.id === targetId)
    if (!draggedStage || !targetStage) return

    // Prevent dragging between active and terminal stages
    if (draggedStage.type !== targetStage.type) return

    const currentGroup = localStages.filter(s => s.type === draggedStage.type)
    const otherGroup = localStages.filter(s => s.type !== draggedStage.type)

    const draggedIndex = currentGroup.findIndex(s => s.id === draggedStageId)
    const targetIndex = currentGroup.findIndex(s => s.id === targetId)

    const reorderedGroup = [...currentGroup]
    reorderedGroup.splice(draggedIndex, 1)
    reorderedGroup.splice(targetIndex, 0, draggedStage)

    const updatedActive = draggedStage.type === 'active' ? reorderedGroup : otherGroup
    const updatedTerminal = draggedStage.type === 'terminal' ? reorderedGroup : otherGroup

    const finalStages: SalesStage[] = []
    let currentPos = 1
    updatedActive.forEach(s => {
      finalStages.push({ ...s, position: currentPos++ })
    })
    updatedTerminal.forEach(s => {
      finalStages.push(s)
    })

    setLocalStages(finalStages)
  }

  const activeStages   = localStages.filter(s => s.type === 'active')
  const terminalStages = localStages.filter(s => s.type === 'terminal')

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Sales Stages"
        breadcrumb={[{ label: 'Settings', to: '/settings' }]}
        showBreadcrumb
        actions={canCreate ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '13px',
              bgcolor: themeColors.primary, '&:hover': { bgcolor: themeColors.primaryDark } }}
          >
            Add Stage
          </Button>
        ) : undefined}
      />

      <Box sx={{ flex: 1, px: 3, pb: 4, maxWidth: 680, overflowY: 'auto' }}>
        {/* Info banner */}
        <Box sx={{
          p: 2, mb: 3, borderRadius: '10px',
          bgcolor: '#eff6ff', border: '1px solid #bfdbfe',
          display: 'flex', gap: 1.5,
        }}>
          <Typography fontSize="13px" color="#1d4ed8" sx={{ lineHeight: 1.6 }}>
            Configure the pipeline stages for your Won Deals. Each sale record moves through active flow stages towards onboarding and completion. 
            Default stage <strong>"New Sale"</strong> is auto-assigned when a deal is marked Won.
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : stages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#fff', borderRadius: '14px',
            border: `2px dashed ${themeColors.border}` }}>
            <Typography fontSize="15px" fontWeight={700} color="text.primary" mb={0.5}>No stages yet</Typography>
            <Typography fontSize="13px" color="text.secondary" mb={3}>
              Create your first sales stage to start tracking won deals
            </Typography>
            {canCreate && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}
                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700,
                  bgcolor: themeColors.primary, '&:hover': { bgcolor: themeColors.primaryDark } }}>
                Add Stage
              </Button>
            )}
          </Box>
        ) : (
          <Stack spacing={3}>
            {/* Active stages */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography fontSize="12px" fontWeight={700} color="text.secondary"
                  textTransform="uppercase" letterSpacing="0.5px">
                  Active Stages ({activeStages.length})
                </Typography>
                <Typography fontSize="11px" color="text.disabled">Reorder Flow</Typography>
              </Box>
              {activeStages.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center', bgcolor: '#f9fafb', borderRadius: '10px', border: '1px dashed #e5e7eb' }}>
                  <Typography fontSize="13px" color="text.secondary">No active stages — add one above</Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {activeStages.map(s => (
                    <StageRow key={s.id} stage={s}
                      onEdit={setEditStage}
                      onDelete={setDeleteStage}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      onDragStart={(e) => handleDragStart(e, s.id)}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, s.id)}
                      isDragging={s.id === draggedStageId}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            {/* Terminal stages */}
            {terminalStages.length > 0 && (
              <Box>
                <Typography fontSize="12px" fontWeight={700} color="text.secondary"
                  textTransform="uppercase" letterSpacing="0.5px" sx={{ mb: 1.5 }}>
                  Terminal Stages ({terminalStages.length})
                </Typography>
                <Stack spacing={1}>
                  {terminalStages.map(s => (
                    <StageRow key={s.id} stage={s}
                      onEdit={setEditStage}
                      onDelete={setDeleteStage}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      onDragStart={(e) => handleDragStart(e, s.id)}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, s.id)}
                      isDragging={s.id === draggedStageId}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </Box>

      {/* Add modal */}
      {addOpen && (
        <StageFormModal
          open={addOpen}
          onClose={() => setAddOpen(false)}
        />
      )}

      {/* Edit modal */}
      {editStage && (
        <StageFormModal
          open={!!editStage}
          onClose={() => setEditStage(null)}
          stage={editStage}
        />
      )}

      {/* Delete dialog */}
      <DeleteDialog
        open={!!deleteStage}
        onClose={() => setDeleteStage(null)}
        onConfirm={() => deleteStage && deleteMut.mutate(deleteStage.id)}
        isDeleting={deleteMut.isPending}
        title="Delete Sales Stage"
        description={`Are you sure you want to delete "${deleteStage?.name}"? Make sure no sale records are currently using this stage.`}
      />

      {/* Save Reorder Confirmation */}
      <ConfirmDialog
        open={isReorderConfirmOpen}
        title="Save Stage Order"
        description="Are you sure you want to save the new order of sales stages?"
        confirmLabel="Save"
        confirmColor="primary"
        onClose={() => setIsReorderConfirmOpen(false)}
        onConfirm={handleConfirmSaveOrder}
      />

      {/* Bottom Floating Action Buttons (No wrapping background/border box) */}
      {hasChanges && (
        <Box sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          display: 'flex',
          gap: 1.5,
          zIndex: 1000,
        }}>
          <Button
            variant="outlined"
            onClick={handleDiscardChanges}
            disabled={reorderMut.isPending}
            sx={{
              borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '13px',
              bgcolor: '#fff', color: '#374151', borderColor: '#d1d5db',
              '&:hover': { bgcolor: '#f9fafb', borderColor: '#cbd5e1' }
            }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            onClick={() => setIsReorderConfirmOpen(true)}
            disabled={reorderMut.isPending}
            sx={{
              borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '13px',
              bgcolor: themeColors.primary, '&:hover': { bgcolor: themeColors.primaryDark }
            }}
          >
            {reorderMut.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}
    </Box>
  )
}
